import { Injectable } from "@angular/core";
import { AiAgentPendingDownload } from "./ai-agent-download";
import {
  AiAgentProduct,
  normalizeAiAgentProduct,
} from "./ai-agent-product";

export interface AiAgentChatLine {
  role: "user" | "assistant" | "system";
  text: string;
  /** Turn-only: Excel downloads shown under the assistant bubble (click to run). */
  downloads?: AiAgentPendingDownload[];
}

const STORAGE_KEY_WAINE = "dws.ai-agent.active-session.v1";
const STORAGE_KEY_GIADA = "dws.ai-agent.active-session.giada.v1";

/**
 * Remembers only the active session id per product (giada | waine).
 * Message history is loaded from backend {@code GET /ai-agent/sessions/{id}}.
 */
@Injectable({ providedIn: "root" })
export class AiAgentChatStore {
  private key(product?: AiAgentProduct | string | null): string {
    return normalizeAiAgentProduct(product) === "giada"
      ? STORAGE_KEY_GIADA
      : STORAGE_KEY_WAINE;
  }

  getActiveSessionId(product?: AiAgentProduct | string | null): string | null {
    try {
      const raw = localStorage.getItem(this.key(product));
      if (!raw) {
        return null;
      }
      if (raw.startsWith("{")) {
        const parsed = JSON.parse(raw);
        return typeof parsed?.sessionId === "string"
          ? parsed.sessionId.trim() || null
          : null;
      }
      return String(raw).trim() || null;
    } catch {
      return null;
    }
  }

  /** Persist active session id (null clears). */
  setActiveSessionId(
    sessionId: string | null | undefined,
    product?: AiAgentProduct | string | null
  ): void {
    try {
      const storageKey = this.key(product);
      const id =
        sessionId == null || sessionId === ""
          ? null
          : String(sessionId).trim() || null;
      if (!id) {
        localStorage.removeItem(storageKey);
        return;
      }
      localStorage.setItem(storageKey, id);
    } catch {
      /* quota / private mode */
    }
  }

  clear(product?: AiAgentProduct | string | null): void {
    this.setActiveSessionId(null, product);
  }
}
